import React, { useEffect, useRef, useState } from 'react';
import { Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
	UploadChangeParam,
	UploadFile,
	UploadProps,
} from 'antd/lib/upload/interface';
import { RcFile, UploadRequestOption } from 'rc-upload/lib/interface';
import axios, { Canceler } from 'axios';
import {
	ControllerRenderProps,
	FieldError,
	useFormContext,
} from 'react-hook-form';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { isFunction, omit } from 'lodash';

import { FileModel, isFileFormatValid, isFileSizeValid } from 'utils/fileUtils';

import styles from './FileUploadDragger.module.less';
import OmniNotification from 'components/OmniNotification';

const CancelToken = axios.CancelToken;

export type FileUploadProps = {
	error?: FieldError;
	uploadLinkFunction: Function;
	field?: ControllerRenderProps;
	onLoadingChange?: (loading: boolean) => void;
	beforeUploadValidation?: (file: RcFile) => Promise<{
		success: boolean;
		errorMessage?: string;
	}>;
	postUploadValidation?: {
		api: Function;
		errorMessage: string;
	};
	acceptedFileFormat?: string[];
	acceptedFileSize?: number;
	hint?: string;
	buttonText?: string;
	onFileListChange?: (files: FileModel[]) => void;
	allowedItemList?: string[];
	disallowedItemsHint?: string;
	strictMaxCount?: boolean; // blocks uploading over max count
	projectName?: string;
	customSuccessCallback?: () => void;
} & UploadProps;

interface ICancelRequests {
	[key: string]: Canceler;
}

interface IFilesStatus {
	[key: string]: boolean;
}
export const getItemsAreAllowed = (allowedItemsGroup, uploadedItemGroups) => {
	let allowed = true;
	const allowedExtensions = Object.keys(allowedItemsGroup);
	allowedExtensions.forEach((extension) => {
		if (
			uploadedItemGroups[extension] > allowedItemsGroup[extension] &&
			extension != 'obb'
		)
			allowed = false;
	});
	return allowed;
};

export default function FileUpload(props: FileUploadProps) {
	const { t } = useTranslation();
	const formContext = useFormContext();
	const {
		maxCount = 1,
		acceptedFileSize,
		acceptedFileFormat,
		uploadLinkFunction,
		defaultFileList = [],
		beforeUploadValidation,
		postUploadValidation,
		className,
		error,
		field,
		onLoadingChange,
		hint,
		buttonText = t('uploader.UploadFile'),
		onFileListChange,
		allowedItemList,
		disallowedItemsHint,
		strictMaxCount,
		projectName,
		customSuccessCallback,
		...rest
	} = props;
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [loadingFileKeys, setLoadingFileKeys] = useState<IFilesStatus>({});
	const isLoading = Object.values(loadingFileKeys).some(Boolean);
	const showError = !isLoading && !!error?.message;
	const acceptedUploadFormat = acceptedFileFormat?.join(',');
	const cancelRef = useRef<ICancelRequests>({});

	useEffect(() => {
		onLoadingChange?.(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps

		if (defaultFileList.length > fileList.length) {
			setFileList(defaultFileList);
		}
	}, [isLoading, props.defaultFileList]);

	const handleLoadingFile = (file: RcFile, isLoading: boolean) =>
		setLoadingFileKeys((loadingFileKeys) => ({
			...loadingFileKeys,
			[file.uid]: isLoading,
		}));

	const removeLoadingFileKey = (fileKey: string) =>
		setLoadingFileKeys((loadingFileKeys) => omit(loadingFileKeys, [fileKey]));

	const handleChange = ({ fileList }: UploadChangeParam) => {
		const newFileList = [...fileList];
		const removedFiles = newFileList.splice(0, newFileList.length - maxCount);

		removedFiles.forEach((file) => handleRemove(file)); // cancel requests for removed files

		setFileList(newFileList);
		handleFileListChange(newFileList);
	};

	const handleFileListChange = (fileList: UploadFile[]) => {
		const files = fileList
			.filter((file) => file.status === 'done' || file.status === 'error')
			.map((file) => ({
				s3Key: file.response?.s3Key,
				fileName: file.name,
			}));
		field?.onChange(files);
		onFileListChange?.(files);
	};
	const isVideoUpload = (file: RcFile) => {
		return getFileExtension(file.name) == 'mp4';
	};
	const handleRequest = async (options: UploadRequestOption) => {
		const { onSuccess, onError, onProgress } = options;
		const file = options.file as RcFile;

		if (acceptedFileFormat && !isFileFormatValid(file, acceptedFileFormat)) {
			onError?.(new Error(t('uploader.fileTypeError')));
			return;
		}

		if (acceptedFileSize && !isFileSizeValid(file, acceptedFileSize)) {
			onError?.(
				new Error(
					acceptedFileSize < 1024
						? t('uploader.sizeError.MB', {
								fileSize: acceptedFileSize,
							})
						: t('uploader.sizeError.GB', {
								fileSize: acceptedFileSize / 1024,
							}),
				),
			);
			return;
		}

		if (!fileList.some((f) => f.uid === file.uid)) return; // call upload API only for the files in the fileList

		handleLoadingFile(file, true);

		try {
			const { key, uploadLink, contentType } = await uploadLinkFunction(
				file.name,
			);

			const result: any = await axios.put(uploadLink, file, {
				headers: {
					'Content-Type': contentType,
				},
				onUploadProgress: (e) => {
					onProgress?.({ percent: (e.loaded / e.total) * 100 } as any);
				},
				cancelToken: new CancelToken(function executor(c) {
					cancelRef.current[file.uid] = c;
				}),
			});

			if (postUploadValidation) {
				try {
					await postUploadValidation.api(key, file.name);
				} catch (error) {
					const e = new Error(postUploadValidation.errorMessage);
					e.name = 'PostUploadError';
					throw e;
				}
			}

			if (isFunction(customSuccessCallback)) {
				onSuccess?.(customSuccessCallback(), result);
			} else if (isVideoUpload(file)) {
				onSuccess?.(
					OmniNotification.success({
						message: t('common.Success.assetUpload', {
							assetType: t('common.Video'),
						}),
						description: t('common.Success.assetUpload.description', {
							assetType: t('common.Video').toLowerCase(),
							projectName: projectName,
						}),
					}),
					result,
				);
			}

			onSuccess?.({ ...result.body, s3Key: key }, result);
		} catch (err: any) {
			onError?.(
				new Error(
					err?.name === 'PostUploadError'
						? err.message
						: t('uploader.uploadError'),
				),
			);
		} finally {
			handleLoadingFile(file, false);
		}
	};

	const cancelRequest = (fileUid: string) => {
		if (!cancelRef.current[fileUid]) return;

		cancelRef.current[fileUid]();

		delete cancelRef.current[fileUid];
	};

	const handleRemove = (file: UploadFile) => {
		const { uid } = file;

		removeLoadingFileKey(uid);
		cancelRequest(uid);

		let newFileList = fileList.filter((value) => value.uid !== file.uid);

		setFileList(newFileList);
		handleFileListChange(newFileList);

		return true;
	};
	const handleBeforeUpload = async (file: RcFile, FileList: RcFile[]) => {
		if (strictMaxCount && FileList.length + fileList.length > maxCount) {
			formContext?.setError(field?.name || '', {
				message: t('uploader.maxCountError', {
					count: maxCount,
				}),
			});
			return Upload.LIST_IGNORE;
		}
		if (!uploadItemsAllowed(FileList)) {
			formContext?.setError(field?.name || '', {
				message: disallowedItemsHint,
			});
			return Upload.LIST_IGNORE;
		}
		if (beforeUploadValidation) {
			const { success, errorMessage } = await beforeUploadValidation(file);
			if (!success) {
				formContext?.setError(field?.name || '', {
					message: errorMessage,
				});
				return Upload.LIST_IGNORE;
			}
		}
	};

	const getFileExtension = (fileName: string) => {
		const extension = fileName.split('.').pop();
		if (extension) return extension;
		return '';
	};

	const groupByExtension = (extensionsList: string[]) => {
		return extensionsList.reduce(
			(acc: { [key: string]: any }, value: string) => {
				if (!acc[value]) {
					acc[value] = 0;
				}
				acc[value]++;
				return acc;
			},
			{},
		);
	};

	const allowedItemsGroup = groupByExtension(allowedItemList ?? []);

	const uploadItemsAllowed = (uploadFileList: RcFile[]) => {
		const uploadedExtensions = fileList.map((uploadedFile) =>
			getFileExtension(uploadedFile.name),
		);
		uploadedExtensions.push(
			...uploadFileList.map((file) => getFileExtension(file.name)),
		);
		const uploadedItemGroups = groupByExtension(uploadedExtensions);

		let itemsAreAllowed = true;
		if (allowedItemsGroup) {
			itemsAreAllowed = getItemsAreAllowed(
				allowedItemsGroup,
				uploadedItemGroups,
			);
		}
		return itemsAreAllowed;
	};

	return (
		<div ref={field?.ref} tabIndex={0} className={cx(className, styles.upload)}>
			<Upload.Dragger
				fileList={fileList}
				data-testid="base-uploader"
				onChange={handleChange}
				customRequest={handleRequest}
				beforeUpload={handleBeforeUpload}
				onRemove={handleRemove}
				accept={acceptedUploadFormat}
				{...rest}
			>
				<UploadOutlined />
				{hint && <div className="label-hint">{hint}</div>}
			</Upload.Dragger>
			{showError && (
				<div role="alert">
					<span className="hint">{error?.message}</span>
				</div>
			)}
		</div>
	);
}
